import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useOrpc } from '@/hooks/useClient'
import type { FrequentFacilityList } from './discoveryTypes'

// lat/lon omitted together = no distances on the rows (ZIP could not be geocoded).
export type FrequentFacilityInput = { lat?: number; lon?: number }

/** The practice's Frequently used imaging facilities, bookmarked first (server order). */
export function useFacilityFrequent(input: FrequentFacilityInput | null) {
  const orpc = useOrpc()
  return useQuery({
    ...orpc.facilityDiscoveryFrequent.queryOptions({ input: input ?? {} }),
    enabled: input !== null,
    staleTime: 30_000,
    retry: 1,
  })
}

/** Adds an adopted facility to the practice's Frequently used list. */
export function useRememberFacility() {
  const orpc = useOrpc()
  const queryClient = useQueryClient()
  return useMutation(
    orpc.facilityDiscoveryRemember.mutationOptions({
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: orpc.facilityDiscoveryFrequent.key() })
      },
    }),
  )
}

/**
 * Pins or unpins a Frequently used facility. The toggle flips at once in every cached
 * Frequently used list (whatever its input) and only that row flips back if the save fails;
 * the refetch once the last pending bookmark settles brings the server's bookmarked-first
 * order without overwriting another row's in-flight flip.
 */
export function useBookmarkFacility() {
  const orpc = useOrpc()
  const queryClient = useQueryClient()
  const frequentKey = orpc.facilityDiscoveryFrequent.key()
  const setBookmarked = (placeId: string, bookmarked: boolean) =>
    queryClient.setQueriesData<FrequentFacilityList>({ queryKey: frequentKey }, (old) =>
      old
        ? {
            ...old,
            facilities: old.facilities.map((f) =>
              f.placeId === placeId ? { ...f, bookmarked } : f,
            ),
          }
        : old,
    )
  return useMutation(
    orpc.facilityDiscoveryBookmark.mutationOptions({
      onMutate: async ({ placeId, bookmarked }) => {
        await queryClient.cancelQueries({ queryKey: frequentKey })
        setBookmarked(placeId, bookmarked)
      },
      onError: (_err, { placeId, bookmarked }) => setBookmarked(placeId, !bookmarked),
      onSettled: () => {
        // This mutation still counts as pending here; refetch only after the last one.
        const mutationKey = orpc.facilityDiscoveryBookmark.mutationKey()
        if (queryClient.isMutating({ mutationKey }) === 1) {
          void queryClient.invalidateQueries({ queryKey: frequentKey })
        }
      },
    }),
  )
}
